import { z } from "zod";
import { AiInsightDraft, InsightVisibility } from "./insight";

// ---------------------------------------------------------------------------
// External-data digest (Phase 2 — Composio insights)
//
// Stage 1 of the `ai-external-signals` generator: a bounded tool-loop agent
// reads a user's connected external tools (CRM, calendar, Slack, …) and
// produces a *digest* of findings — not insights. The digest is then fed,
// clearly delimited as untrusted data, into the structured synthesis stage.
// Hard length/count caps double as a prompt-injection blast-radius bound.
// ---------------------------------------------------------------------------

/** Whether a finding describes org-level data (CRM pipeline, Slack channels)
 *  or the connecting user's personal data (their calendar, their inbox). */
export const ExternalDataClassification = z.enum(["org", "personal"]);
export type ExternalDataClassification = z.infer<
  typeof ExternalDataClassification
>;

// Caps below truncate instead of rejecting: the digest is pure model output,
// and a schema `.max()` would discard a whole user's gather over one extra
// element or a slightly long summary (see MEMORY_MAX_OPS_PER_RUN rationale).
// The `.describe()` hints keep steering the model.

export const ExternalDigestItem = z.object({
  /** Uppercase Composio toolkit slug the finding came from, e.g. "HUBSPOT". */
  toolkit: z.string().min(1),
  classification: ExternalDataClassification,
  title: z
    .string()
    .min(1)
    .describe("Kort titel, max 80 tecken")
    .transform((s) => s.slice(0, 80)),
  summary: z
    .string()
    .min(1)
    .describe("Konkret sammanfattning, max 300 tecken")
    .transform((s) => s.slice(0, 300)),
  evidence: z
    .array(z.string().transform((s) => s.slice(0, 200)))
    .describe("Max 3 korta punkter")
    .default([])
    .transform((a) => a.slice(0, 3)),
});
export type ExternalDigestItem = z.infer<typeof ExternalDigestItem>;

export const ExternalUserDigest = z.object({
  uid: z.string().min(1),
  memberName: z.string().nullable().default(null),
  /** Toolkits that actually contributed findings. */
  toolkits: z.array(z.string()).default([]),
  items: z
    .array(ExternalDigestItem)
    .describe("Max 10 fynd")
    .transform((a) => a.slice(0, 10)),
  fetchedAt: z.string(),
});
export type ExternalUserDigest = z.infer<typeof ExternalUserDigest>;

/**
 * Synthesis-stage draft: like {@link AiInsightDraft} but without scope/member
 * targeting (external insights are business-scoped) and with the visibility +
 * attribution fields the generator validates before persisting.
 */
export const ExternalInsightDraft = AiInsightDraft.omit({
  scope: true,
  memberId: true,
  salesTeamId: true,
  // External insights are synthesised from connected-account digests, never
  // from the vault — attribution here is `sourceToolkits`.
  sourceDocumentTitles: true,
}).extend({
  visibility: InsightVisibility,
  /** Required when visibility is "owner": uid of the connecting user. */
  ownerUid: z.string().nullable().default(null),
  sourceToolkits: z
    .array(z.string())
    .default([])
    .transform((a) => a.slice(0, 4)),
});
export type ExternalInsightDraft = z.infer<typeof ExternalInsightDraft>;

export const ExternalInsightBatch = z.object({
  insights: z
    .array(ExternalInsightDraft)
    .describe("Max 6 insikter")
    .transform((a) => a.slice(0, 6)),
});
export type ExternalInsightBatch = z.infer<typeof ExternalInsightBatch>;
